import styled from 'styled-components';
import parseZone from 'helpers/parseZone';
import { ModalContentProps } from 'components/remodal/v2/Modal';
import { Booking, BookingModification } from 'swagger/models';
import { useCloseModal } from 'components/remodal/v2/hooks';
import { useRef } from 'react';
import Button from 'components/ui/Button/Button';
import { mediaMSmMax } from '../../helpers/breakpoints';

const ConfirmEditBookingModal = ({
  booking,
  isApproveAction = false,
  targetUserName = '',
  bookingModification,
  onCloseModal,
}: ModalContentProps<{
  booking: Booking;
  isApproveAction?: boolean;
  targetUserName?: string;
  bookingModification: BookingModification;
}>) => {
  const closeModal = useCloseModal(onCloseModal);

  const prevLatestModification = useRef(bookingModification).current;
  const prevBookingTrip = useRef(booking).current;

  const getHeading = () => {
    if (prevBookingTrip?.state === 'approved') {
      if (isApproveAction) return 'Your booking has been updated';
      return `You declined ${targetUserName}'s edit request`;
    }
    if (isApproveAction) return 'Your booking has been updated and approved';
    return `You declined ${targetUserName}'s edit request`;
  };

  const getContent = () => {
    const prevStart = parseZone(prevBookingTrip?.trip_start);
    const start = parseZone(prevLatestModification.trip_start);

    const timeChange = prevStart.format('h:mm A') !== start.format('h:mm A');
    const dateChange = prevStart.diff(start, 'day') !== 0;

    if (prevBookingTrip.state === 'approved') {
      if (isApproveAction)
        return (
          <span>
            The {dateChange ? 'new' : ''} booking start date is <b>{start.format('MMM D, YYYY')}</b> and the{' '}
            {timeChange ? 'new' : ''} start time is <b>{start.format('h:mm A')}</b>.
          </span>
        );
      return `The requested edits to your booking have not been applied and the original booking details will remain.`;
    }
    if (isApproveAction)
      return (
        <span>
          Your booking is now approved. The {dateChange ? 'new' : ''} booking start date is{' '}
          <b>{start.format('MMM D, YYYY')}</b> and the {timeChange ? 'new' : ''} start time is{' '}
          <b>{start.format('h:mm A')}</b>.
        </span>
      );
    return `The requested edits to your booking request have not been applied and the original booking details will remain. ${targetUserName} can still approve or decline your booking request.`;
  };

  return (
    <Wrapper>
      <SvgWrapper>
        <SuccessSVG />
      </SvgWrapper>
      <Heading>{getHeading()}</Heading>
      <Description>{getContent()}</Description>
      <ButtonsWrapper>
        <Button fullWidth text="Got it" variant="yellow" onClick={closeModal} />
      </ButtonsWrapper>
    </Wrapper>
  );
};

const SvgWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 15px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 20px;
  padding-top: 30px;

  ${mediaMSmMax} {
    padding-top: 30px;
    flex-flow: column;
  }

  button {
    &:first-child {
      width: 75%;
    }
    &:last-child {
      width: 25%;
    }
  }
`;

const SuccessSVG = () => (
  <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 0C9.41306 0 0 9.43408 0 20.9999C0 32.5869 9.43416 42 21 42C32.5869 42 42 32.5658 42 20.9999C42.0211 9.41298 32.5869 0 21 0ZM21 38.8342C11.1648 38.8342 3.16583 30.8351 3.16583 20.9999C3.16583 11.1647 11.1648 3.16583 21 3.16583C30.8351 3.16583 38.8341 11.1647 38.8341 20.9999C38.8552 30.8351 30.8351 38.8342 21 38.8342ZM30.7085 14.2673C31.3206 14.9004 31.2995 15.8924 30.6874 16.5044L19.1427 27.7537C18.8261 28.0492 18.4462 28.197 18.0452 28.197C17.6442 28.197 17.2432 28.0492 16.9266 27.7537L11.3548 22.2663C10.7216 21.6543 10.7216 20.6412 11.3337 20.0292C11.9457 19.4171 12.9588 19.396 13.5708 20.008L18.0452 24.398L28.4924 14.2251C29.0834 13.6342 30.0965 13.6341 30.7085 14.2673Z"
      fill="#2A8500"
    />
  </svg>
);

const Wrapper = styled.div``;

const Heading = styled.h1`
  font-size: 24px;
  text-align: center;
  margin-bottom: 10px;
  font-weight: 600;

  ${mediaMSmMax} {
    font-weight: 600;
    font-size: 20px;

    br {
      display: none;
    }
  }
`;

const Description = styled.div`
  text-align: center;
`;

export default ConfirmEditBookingModal;
